import React, { useState } from "react";
import { motion } from "framer-motion";

import {
  ArrowUpRight,
  Code2,
  Globe2,
  Database,
  Server,
  ShieldCheck,
  Cloud,
  Layers3,
  GitBranch,
  Zap,
  CheckCircle2,
  Boxes,
  LockKeyhole,
} from "lucide-react";

import { softwareDevelopmentData } from "../../../../data/SoftwareDevelopmentData";


import ContactModal from "../../../../Assets/Common/ContactModal";

// ============================================================
// ANIMATION VARIANTS
// ============================================================

const containerVariants = {
  hidden: {},
  visible: {
    transition: {
      staggerChildren: 0.1,
    },
  },
};

const fadeUp = {
  hidden: {
    opacity: 0,
    y: 28,
  },

  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.7,
      ease: [0.22, 1, 0.36, 1],
    },
  },
};

// ============================================================
// ARCHITECTURE NODE
// ============================================================

const ArchitectureNode = ({
  icon: Icon,
  title,
  subtitle,
  position,
  active = false,
}) => {
  return (
    <motion.div
      initial={{
        opacity: 0,
        scale: 0.85,
        y: 15,
      }}
      animate={{
        opacity: 1,
        scale: 1,
        y: 0,
      }}
      transition={{
        duration: 0.6,
        ease: [0.22, 1, 0.36, 1],
        delay: position.delay,
      }}
      className={`absolute ${position.className}`}
    >
      <motion.div
        animate={
          active
            ? {
                boxShadow: [
                  "0 0 0 rgba(249,115,22,0)",
                  "0 0 30px rgba(249,115,22,0.12)",
                  "0 0 0 rgba(249,115,22,0)",
                ],
              }
            : {}
        }
        transition={{
          duration: 2.8,
          repeat: Infinity,
          ease: "easeInOut",
        }}
        className="
          group
          flex
          w-[86px]
          min-w-0
          items-center
          gap-1.5
          rounded-lg
          border
          border-[var(--border-primary)]
          bg-[var(--card-bg)]/[0.95]
          px-2
          py-2
          shadow-[0_12px_30px_var(--shadow-color)]
          backdrop-blur-xl
          transition-all
          duration-300
          hover:border-orange-500/30

          min-[360px]:w-[94px]
          min-[360px]:gap-2
          min-[360px]:px-2.5

          sm:w-[105px]
          sm:gap-2.5
          sm:rounded-xl
          sm:px-3
          sm:py-2.5
        "
      >
        {/* ICON */}

        <div
          className="
            flex
            h-6
            w-6
            shrink-0
            items-center
            justify-center
            rounded-md
            border
            border-orange-500/10
            bg-orange-500/[0.08]
            text-orange-400
            transition-all
            duration-300
            group-hover:bg-orange-500
            group-hover:text-black

            min-[360px]:h-7
            min-[360px]:w-7

            sm:h-8
            sm:w-8
            sm:rounded-lg
          "
        >
          <Icon
            size={11}
            strokeWidth={1.8}
            className="min-[360px]:h-3 min-[360px]:w-3 sm:h-3.5 sm:w-3.5"
          />
        </div>

        {/* TEXT */}

        <div className="min-w-0 flex-1">
          <p
            className="
              truncate
              text-[7px]
              font-semibold
              text-[var(--text-primary)]/[0.80]

              min-[360px]:text-[7.5px]

              sm:text-[8px]
            "
          >
            {title}
          </p>

          <p
            className="
              mt-0.5
              truncate
              text-[5.5px]
              text-[var(--text-muted)]/[0.45]

              min-[360px]:text-[6px]

              sm:text-[6.5px]
            "
          >
            {subtitle}
          </p>
        </div>
      </motion.div>
    </motion.div>
  );
};

// ============================================================
// CONNECTION LINE
// ============================================================

const ConnectionLine = ({
  className,
  delay = 0,
  reverse = false,
}) => {
  return (
    <div
      className={`pointer-events-none absolute ${className}`}
    >
      <motion.div
        initial={{
          scaleX: 0,
          opacity: 0,
        }}
        animate={{
          scaleX: 1,
          opacity: [0.2, 0.65, 0.2],
        }}
        transition={{
          scaleX: {
            duration: 0.9,
            delay,
            ease: "easeOut",
          },

          opacity: {
            duration: 2.5,
            delay: delay + 0.8,
            repeat: Infinity,
            ease: "easeInOut",
          },
        }}
        style={{
          transformOrigin: reverse
            ? "right center"
            : "left center",
        }}
        className="
          h-px
          w-full
          bg-gradient-to-r
          from-orange-500/10
          via-orange-500/60
          to-orange-500/10
        "
      />
    </div>
  );
};

// ============================================================
// PREMIUM SOFTWARE ARCHITECTURE VISUAL
// ============================================================

const HeroSoftwareVisual = () => {
  return (
    <div
      className="
        relative
        mx-auto
        w-full
        max-w-[560px]
        px-1
        pb-3
        sm:px-2
        sm:pb-4
      "
    >
      {/* ======================================================
          AMBIENT GLOWS
      ======================================================= */}

      <div
        className="
          pointer-events-none
          absolute
          left-1/2
          top-1/2
          h-[230px]
          w-[230px]
          -translate-x-1/2
          -translate-y-1/2
          rounded-full
          bg-orange-500/[0.075]
          blur-[80px]

          min-[400px]:h-[270px]
          min-[400px]:w-[270px]

          sm:h-[330px]
          sm:w-[330px]
          sm:blur-[110px]
        "
      />

      <div
        className="
          pointer-events-none
          absolute
          right-0
          top-0
          h-24
          w-24
          rounded-full
          bg-orange-400/[0.045]
          blur-[60px]

          sm:h-36
          sm:w-36
          sm:blur-[80px]
        "
      />

      <div
        className="
          pointer-events-none
          absolute
          bottom-0
          left-0
          h-24
          w-24
          rounded-full
          bg-orange-600/[0.04]
          blur-[55px]

          sm:h-32
          sm:w-32
          sm:blur-[75px]
        "
      />

      {/* ======================================================
          FLOATING PARTICLES
      ======================================================= */}

      <motion.span
        animate={{
          y: [0, -10, 0],
          opacity: [0.2, 0.8, 0.2],
        }}
        transition={{
          duration: 3,
          repeat: Infinity,
          ease: "easeInOut",
        }}
        className="
          absolute
          left-[7%]
          top-[22%]
          h-1
          w-1
          rounded-full
          bg-orange-400

          sm:h-1.5
          sm:w-1.5
        "
      />

      <motion.span
        animate={{
          y: [0, 8, 0],
          opacity: [0.15, 0.65, 0.15],
        }}
        transition={{
          duration: 3.8,
          repeat: Infinity,
          ease: "easeInOut",
          delay: 0.5,
        }}
        className="
          absolute
          right-[7%]
          top-[18%]
          h-1
          w-1
          rounded-full
          bg-orange-300
        "
      />

      <motion.span
        animate={{
          y: [0, -7, 0],
          opacity: [0.15, 0.6, 0.15],
        }}
        transition={{
          duration: 4.2,
          repeat: Infinity,
          ease: "easeInOut",
          delay: 0.9,
        }}
        className="
          absolute
          bottom-[18%]
          left-[14%]
          h-1
          w-1
          rounded-full
          bg-orange-400
        "
      />

      {/* ======================================================
          MAIN DASHBOARD
      ======================================================= */}

      <motion.div
        initial={{
          opacity: 0,
          y: 35,
          scale: 0.96,
        }}
        animate={{
          opacity: 1,
          y: 0,
          scale: 1,
        }}
        transition={{
          duration: 0.9,
          ease: [0.22, 1, 0.36, 1],
        }}
        className="
          relative
          w-full
          overflow-hidden
          rounded-[16px]
          border
          border-[var(--border-primary)]
          bg-[var(--card-bg)]
          shadow-[0_25px_70px_var(--shadow-color)]

          min-[400px]:rounded-[18px]

          sm:rounded-[22px]
          sm:shadow-[0_35px_100px_var(--shadow-color)]
        "
      >
        {/* ====================================================
            TOP BAR
        ===================================================== */}

        <div
          className="
            flex
            h-9
            items-center
            justify-between
            border-b
            border-[var(--border-secondary)]
            bg-[var(--text-primary)]/[0.025]
            px-2.5

            min-[360px]:h-10
            min-[360px]:px-3

            sm:h-12
            sm:px-5
          "
        >
          {/* MAC DOTS */}

          <div className="flex items-center gap-1">
            <span
              className="
                h-1.5
                w-1.5
                rounded-full
                bg-red-400/60

                sm:h-2.5
                sm:w-2.5
              "
            />

            <span
              className="
                h-1.5
                w-1.5
                rounded-full
                bg-yellow-400/60

                sm:h-2.5
                sm:w-2.5
              "
            />

            <span
              className="
                h-1.5
                w-1.5
                rounded-full
                bg-green-400/60

                sm:h-2.5
                sm:w-2.5
              "
            />
          </div>

          {/* TITLE */}

          <div className="flex items-center gap-1">
            <Boxes
              size={9}
              className="text-orange-400 sm:h-3 sm:w-3"
            />

            <span
              className="
                text-[5px]
                font-semibold
                uppercase
                tracking-[0.12em]
                text-[var(--text-muted)]

                min-[360px]:text-[6px]

                sm:text-[7px]
                sm:tracking-[0.18em]
              "
            >
              Software Architecture
            </span>
          </div>

          {/* LIVE */}

          <div
            className="
              flex
              items-center
              gap-1
              rounded-full
              border
              border-green-500/15
              bg-green-500/[0.06]
              px-1.5
              py-0.5

              sm:gap-1.5
              sm:px-2
              sm:py-1
            "
          >
            <span
              className="
                h-1
                w-1
                rounded-full
                bg-green-400

                sm:h-1.5
                sm:w-1.5
              "
            />

            <span
              className="
                text-[5px]
                font-medium
                text-green-400/80

                sm:text-[6px]
              "
            >
              LIVE
            </span>
          </div>
        </div>

        {/* ====================================================
            DASHBOARD CONTENT
        ===================================================== */}

        <div
          className="
            p-2.5

            min-[360px]:p-3

            min-[400px]:p-3.5

            sm:p-5
          "
        >
          {/* ==================================================
              HEADER
          =================================================== */}

          <div className="flex items-start justify-between gap-2.5 sm:gap-4">
            {/* LEFT */}

            <div className="min-w-0">
              <div className="flex items-center gap-1.5 sm:gap-2">
                <div
                  className="
                    flex
                    h-6
                    w-6
                    shrink-0
                    items-center
                    justify-center
                    rounded-md
                    bg-orange-500/[0.09]
                    text-orange-400

                    min-[360px]:h-7
                    min-[360px]:w-7

                    sm:h-8
                    sm:w-8
                    sm:rounded-lg
                  "
                >
                  <Layers3
                    size={11}
                    className="min-[360px]:h-3 sm:h-3.5"
                  />
                </div>

                <div className="min-w-0">
                  <p
                    className="
                      truncate
                      text-[7px]
                      font-semibold
                      text-[var(--text-primary)]

                      min-[360px]:text-[8px]

                      sm:text-[9px]
                    "
                  >
                    Product Architecture
                  </p>

                  <p
                    className="
                      mt-0.5
                      truncate
                      text-[5px]
                      text-[var(--text-muted)]

                      min-[360px]:text-[5.5px]

                      sm:text-[6.5px]
                    "
                  >
                    Scalable digital infrastructure
                  </p>
                </div>
              </div>
            </div>

            {/* BRANCH */}

            <div
              className="
                flex
                shrink-0
                items-center
                gap-1
                rounded-md
                border
                border-orange-500/10
                bg-orange-500/[0.045]
                px-1.5
                py-1

                min-[360px]:gap-1.5
                min-[360px]:px-2
                min-[360px]:py-1.5

                sm:rounded-lg
                sm:px-2
              "
            >
              <GitBranch
                size={8}
                className="text-orange-400 sm:h-2.5 sm:w-2.5"
              />

              <span
                className="
                  hidden
                  text-[6px]
                  text-[var(--text-muted)]

                  min-[360px]:block

                  sm:text-[6px]
                "
              >
                main / production
              </span>

              <span
                className="
                  block
                  text-[6px]
                  text-[var(--text-muted)]

                  min-[360px]:hidden
                "
              >
                main
              </span>
            </div>
          </div>

          {/* ==================================================
              ARCHITECTURE AREA
          =================================================== */}

          <div
            className="
              relative
              mt-3
              h-[180px]
              min-[360px]:h-[200px]
              overflow-hidden
              rounded-lg
              border
              border-[var(--border-secondary)]
              bg-[var(--bg-secondary)]

              min-[360px]:mt-4

              min-[400px]:h-[215px]

              sm:mt-5
              sm:h-[245px]
              sm:rounded-xl
            "
          >
            {/* GRID */}

            <div
              className="
                pointer-events-none
                absolute
                inset-0
                opacity-[0.025]
                [background-image:linear-gradient(rgba(128,128,128,.6)_1px,transparent_1px),linear-gradient(90deg,rgba(128,128,128,.6)_1px,transparent_1px)]
                [background-size:25px_25px]

                sm:[background-size:35px_35px]
              "
            />

            {/* CENTER GLOW */}

            <div
              className="
                pointer-events-none
                absolute
                left-1/2
                top-1/2
                h-20
                w-20
                -translate-x-1/2
                -translate-y-1/2
                rounded-full
                bg-orange-500/[0.07]
                blur-[40px]

                sm:h-28
                sm:w-28
                sm:blur-[55px]
              "
            />

            {/* =================================================
                CONNECTION LINES
            ================================================== */}

            <ConnectionLine
              className="
                left-[29%]
                top-[34%]
                w-[17%]
                rotate-[18deg]
              "
              delay={0.5}
            />

            <ConnectionLine
              className="
                right-[29%]
                top-[34%]
                w-[17%]
                -rotate-[18deg]
              "
              delay={0.8}
              reverse
            />

            <ConnectionLine
              className="
                left-[29%]
                top-[64%]
                w-[17%]
                -rotate-[18deg]
              "
              delay={1}
            />

            <ConnectionLine
              className="
                right-[29%]
                top-[64%]
                w-[17%]
                rotate-[18deg]
              "
              delay={1.2}
              reverse
            />

            {/* =================================================
                CENTER CORE
            ================================================== */}

            <motion.div
              initial={{
                opacity: 0,
                scale: 0.8,
              }}
              animate={{
                opacity: 1,
                scale: 1,
              }}
              transition={{
                delay: 0.5,
                duration: 0.6,
              }}
              className="
                absolute
                left-1/2
                top-1/2
                z-10
                -translate-x-1/2
                -translate-y-1/2
              "
            >
              <motion.div
                animate={{
                  boxShadow: [
                    "0 0 0 rgba(249,115,22,0)",
                    "0 0 35px rgba(249,115,22,0.18)",
                    "0 0 0 rgba(249,115,22,0)",
                  ],
                }}
                transition={{
                  duration: 2.8,
                  repeat: Infinity,
                  ease: "easeInOut",
                }}
                className="
                  flex
                  h-12
                  w-12
                  flex-col
                  items-center
                  justify-center
                  rounded-xl
                  border
                  border-orange-500/25
                  bg-[var(--card-bg-secondary)]
                  shadow-[0_15px_40px_var(--shadow-color)]

                  min-[360px]:h-14
                  min-[360px]:w-14

                  sm:h-[72px]
                  sm:w-[72px]
                  sm:rounded-2xl
                "
              >
                <div
                  className="
                    flex
                    h-6
                    w-6
                    items-center
                    justify-center
                    rounded-md
                    bg-orange-500
                    text-black

                    min-[360px]:h-7
                    min-[360px]:w-7

                    sm:h-8
                    sm:w-8
                    sm:rounded-lg
                  "
                >
                  <Code2
                    size={12}
                    className="min-[360px]:h-3.5 sm:h-4"
                    strokeWidth={2}
                  />
                </div>

                <span
                  className="
                    mt-1
                    text-[5px]
                    font-semibold
                    uppercase
                    tracking-[0.1em]
                    text-[var(--text-secondary)]

                    sm:mt-1.5
                    sm:text-[6px]
                    sm:tracking-[0.12em]
                  "
                >
                  Core
                </span>
              </motion.div>
            </motion.div>

            {/* =================================================
                FRONTEND NODE
            ================================================== */}

            <ArchitectureNode
              icon={Globe2}
              title="Frontend"
              subtitle="Web Applications"
              active
              position={{
                className: `
                  left-[3%]
                  top-[19%]

                  sm:left-[4%]
                  sm:top-[21%]
                `,
                delay: 0.65,
              }}
            />

            {/* =================================================
                BACKEND NODE
            ================================================== */}

            <ArchitectureNode
              icon={Server}
              title="Backend"
              subtitle="APIs & Services"
              active
              position={{
                className: `
                  right-[3%]
                  top-[19%]

                  sm:right-[4%]
                  sm:top-[21%]
                `,
                delay: 0.8,
              }}
            />

            {/* =================================================
                DATABASE NODE
            ================================================== */}

            <ArchitectureNode
              icon={Database}
              title="Database"
              subtitle="Secure Data"
              position={{
                className: `
                  left-[3%]
                  bottom-[18%]

                  sm:left-[4%]
                  sm:bottom-[19%]
                `,
                delay: 1,
              }}
            />

            {/* =================================================
                CLOUD NODE
            ================================================== */}

            <ArchitectureNode
              icon={Cloud}
              title="Cloud"
              subtitle="Deployment"
              position={{
                className: `
                  right-[3%]
                  bottom-[18%]

                  sm:right-[4%]
                  sm:bottom-[19%]
                `,
                delay: 1.15,
              }}
            />

            {/* =================================================
                SMALL STATUS LABEL
            ================================================== */}

            <motion.div
              initial={{
                opacity: 0,
                y: 8,
              }}
              animate={{
                opacity: 1,
                y: 0,
              }}
              transition={{
                delay: 1.3,
                duration: 0.5,
              }}
              className="
                absolute
                bottom-2
                left-1/2
                -translate-x-1/2
                rounded-full
                border
                border-[var(--border-secondary)]
                bg-[var(--text-primary)]/[0.025]
                px-2
                py-0.5

                min-[360px]:px-2.5
                min-[360px]:py-1

                sm:bottom-2.5
                sm:px-3
              "
            >
              <span
                className="
                  flex
                  items-center
                  gap-1
                  whitespace-nowrap
                  text-[5px]
                  text-[var(--text-muted)]

                  sm:gap-1.5
                  sm:text-[6px]
                "
              >
                <Zap
                  size={6}
                  className="text-orange-400 sm:h-2 sm:w-2"
                />

                Connected Architecture
              </span>
            </motion.div>
          </div>

          {/* ==================================================
              BOTTOM METRICS
          =================================================== */}

          <div
            className="
              mt-2
              grid
              grid-cols-3
              gap-1.5

              min-[360px]:mt-3
              min-[360px]:gap-2

              sm:mt-4
            "
          >
            {/* METRIC 1 */}

            <div
              className="
                min-w-0
                rounded-lg
                border
                border-[var(--border-secondary)]
                bg-[var(--text-primary)]/[0.02]
                px-1.5
                py-2

                min-[360px]:px-2
                min-[360px]:py-2.5

                sm:rounded-xl
                sm:px-3
              "
            >
              <div className="flex items-center justify-between gap-1">
                <span
                  className="
                    truncate
                    text-[4.5px]
                    uppercase
                    tracking-[0.06em]
                    text-[var(--text-muted)]

                    min-[360px]:text-[5px]

                    sm:text-[6px]
                    sm:tracking-[0.1em]
                  "
                >
                  Development
                </span>

                <Code2
                  size={7}
                  className="shrink-0 text-orange-400/70 sm:h-2.5 sm:w-2.5"
                />
              </div>

              <div className="mt-1.5 flex items-end justify-between gap-1 sm:mt-2">
                <span
                  className="
                    text-[10px]
                    font-semibold
                    text-[var(--text-primary)]

                    min-[360px]:text-xs

                    sm:text-sm
                  "
                >
                  92%
                </span>

                <span
                  className="
                    text-[4.5px]
                    text-green-400/70

                    min-[360px]:text-[5px]

                    sm:text-[6px]
                  "
                >
                  +18%
                </span>
              </div>

              <div className="mt-1 h-0.5 overflow-hidden rounded-full bg-[var(--border-primary)] sm:mt-1.5 sm:h-1">
                <motion.div
                  initial={{
                    width: 0,
                  }}
                  animate={{
                    width: "92%",
                  }}
                  transition={{
                    delay: 1,
                    duration: 1.2,
                    ease: "easeOut",
                  }}
                  className="
                    h-full
                    rounded-full
                    bg-gradient-to-r
                    from-orange-600
                    to-orange-400
                  "
                />
              </div>
            </div>

            {/* METRIC 2 */}

            <div
              className="
                min-w-0
                rounded-lg
                border
                border-[var(--border-secondary)]
                bg-[var(--text-primary)]/[0.02]
                px-1.5
                py-2

                min-[360px]:px-2
                min-[360px]:py-2.5

                sm:rounded-xl
                sm:px-3
              "
            >
              <div className="flex items-center justify-between gap-1">
                <span
                  className="
                    truncate
                    text-[4.5px]
                    uppercase
                    tracking-[0.06em]
                    text-[var(--text-muted)]

                    min-[360px]:text-[5px]

                    sm:text-[6px]
                    sm:tracking-[0.1em]
                  "
                >
                  Security
                </span>

                <ShieldCheck
                  size={7}
                  className="shrink-0 text-green-400/70 sm:h-2.5 sm:w-2.5"
                />
              </div>

              <div className="mt-1.5 flex items-center gap-1 sm:mt-2 sm:gap-1.5">
                <CheckCircle2
                  size={9}
                  className="shrink-0 text-green-400 sm:h-[13px] sm:w-[13px]"
                />

                <span
                  className="
                    truncate
                    text-[6px]
                    font-semibold
                    text-[var(--text-primary)]/[0.75]

                    min-[360px]:text-[7px]

                    sm:text-[9px]
                  "
                >
                  Protected
                </span>
              </div>

              <p
                className="
                  mt-1
                  truncate
                  text-[4.5px]
                  text-[var(--text-muted)]

                  min-[360px]:text-[5px]

                  sm:text-[6px]
                "
              >
                Secure architecture
              </p>
            </div>

            {/* METRIC 3 */}

            <div
              className="
                min-w-0
                rounded-lg
                border
                border-[var(--border-secondary)]
                bg-[var(--text-primary)]/[0.02]
                px-1.5
                py-2

                min-[360px]:px-2
                min-[360px]:py-2.5

                sm:rounded-xl
                sm:px-3
              "
            >
              <div className="flex items-center justify-between gap-1">
                <span
                  className="
                    truncate
                    text-[4.5px]
                    uppercase
                    tracking-[0.06em]
                    text-[var(--text-muted)]

                    min-[360px]:text-[5px]

                    sm:text-[6px]
                    sm:tracking-[0.1em]
                  "
                >
                  Performance
                </span>

                <Zap
                  size={7}
                  className="shrink-0 text-orange-400/70 sm:h-2.5 sm:w-2.5"
                />
              </div>

              <div className="mt-1.5 flex items-center gap-1 sm:mt-2">
                <span
                  className="
                    text-[10px]
                    font-semibold
                    text-[var(--text-primary)]

                    min-[360px]:text-xs

                    sm:text-sm
                  "
                >
                  99.9%
                </span>
              </div>

              <p
                className="
                  mt-1
                  truncate
                  text-[4.5px]
                  text-[var(--text-muted)]

                  min-[360px]:text-[5px]

                  sm:text-[6px]
                "
              >
                Reliable infrastructure
              </p>
            </div>
          </div>

          {/* ==================================================
              TECHNOLOGY STACK
          =================================================== */}

          <div
            className="
              mt-2.5
              flex
              flex-col
              items-start
              gap-2

              min-[400px]:flex-row
              min-[400px]:items-center
              min-[400px]:justify-between

              sm:mt-4
            "
          >
            {/* LABEL */}

            <div className="flex items-center gap-2">
              <span
                className="
                  text-[5px]
                  font-medium
                  uppercase
                  tracking-[0.1em]
                  text-[var(--text-muted)]

                  sm:text-[6px]
                  sm:tracking-[0.15em]
                "
              >
                Technology Stack
              </span>

              <span className="hidden h-px w-8 bg-[var(--border-primary)] min-[400px]:block" />
            </div>

            {/* TECHNOLOGIES */}

            <div className="flex max-w-full items-center gap-1 overflow-x-auto pb-0.5 scrollbar-hide">
              {["React", "Node", "API", "Cloud"].map(
                (tech, index) => (
                  <motion.span
                    key={tech}
                    initial={{
                      opacity: 0,
                      y: 5,
                    }}
                    animate={{
                      opacity: 1,
                      y: 0,
                    }}
                    transition={{
                      delay: 1.2 + index * 0.1,
                    }}
                    className="
                      shrink-0
                      rounded-md
                      border
                      border-[var(--border-secondary)]
                      bg-[var(--text-primary)]/[0.025]
                      px-1.5
                      py-0.5
                      text-[5px]
                      text-[var(--text-muted)]

                      min-[360px]:px-2
                      min-[360px]:py-1

                      sm:text-[6px]
                    "
                  >
                    {tech}
                  </motion.span>
                )
              )}
            </div>
          </div>
        </div>
      </motion.div>

      {/* ======================================================
          FLOATING PRODUCTION CARD
      ======================================================= */}

      <motion.div
        initial={{
          opacity: 0,
          x: 25,
        }}
        animate={{
          opacity: 1,
          x: 0,
        }}
        transition={{
          delay: 0.7,
          duration: 0.7,
          ease: [0.22, 1, 0.36, 1],
        }}
        className="
          absolute
          -right-1
          -top-3
          z-20
          rounded-lg
          border
          border-orange-500/20
          bg-[var(--card-bg)]/[0.95]
          px-2
          py-2
          shadow-[0_15px_40px_var(--shadow-color)]
          backdrop-blur-xl

          min-[360px]:-right-1.5
          min-[360px]:-top-4
          min-[360px]:px-2.5
          min-[360px]:py-2.5

          sm:-right-5
          sm:-top-5
          sm:rounded-xl
          sm:px-3.5
          sm:py-3
        "
      >
        <div className="flex items-center gap-1.5 sm:gap-2.5">
          <div
            className="
              flex
              h-6
              w-6
              shrink-0
              items-center
              justify-center
              rounded-md
              bg-orange-500/[0.09]
              text-orange-400

              min-[360px]:h-7
              min-[360px]:w-7

              sm:h-8
              sm:w-8
              sm:rounded-lg
            "
          >
            <Zap
              size={10}
              className="min-[360px]:h-3 sm:h-3.5"
            />
          </div>

          <div className="min-w-0">
            <div className="flex items-center gap-1">
              <p
                className="
                  whitespace-nowrap
                  text-[6px]
                  font-semibold
                  text-[var(--text-primary)]/[0.80]

                  min-[360px]:text-[7px]

                  sm:text-[8px]
                "
              >
                Production Ready
              </p>

              <span className="h-1 w-1 shrink-0 rounded-full bg-green-400 sm:h-1.5 sm:w-1.5" />
            </div>

            <p
              className="
                mt-0.5
                whitespace-nowrap
                text-[5px]
                text-[var(--text-muted)]

                sm:text-[7px]
              "
            >
              Built for real-world scale
            </p>
          </div>
        </div>
      </motion.div>

      {/* ======================================================
          FLOATING SECURITY CARD
      ======================================================= */}

      <motion.div
        initial={{
          opacity: 0,
          x: -25,
        }}
        animate={{
          opacity: 1,
          x: 0,
        }}
        transition={{
          delay: 0.9,
          duration: 0.7,
          ease: [0.22, 1, 0.36, 1],
        }}
        className="
          absolute
          -bottom-2
          -left-1
          z-20
          rounded-lg
          border
          border-[var(--border-primary)]
          bg-[var(--card-bg)]/[0.95]
          px-2
          py-2
          shadow-[0_15px_40px_var(--shadow-color)]
          backdrop-blur-xl

          min-[360px]:-bottom-3
          min-[360px]:-left-1.5
          min-[360px]:px-2.5
          min-[360px]:py-2.5

          sm:-bottom-5
          sm:-left-5
          sm:rounded-xl
          sm:px-3.5
          sm:py-3
        "
      >
        <div className="flex items-center gap-1.5 sm:gap-2.5">
          <div
            className="
              flex
              h-6
              w-6
              shrink-0
              items-center
              justify-center
              rounded-md
              bg-green-500/[0.07]
              text-green-400

              min-[360px]:h-7
              min-[360px]:w-7

              sm:h-8
              sm:w-8
              sm:rounded-lg
            "
          >
            <LockKeyhole
              size={10}
              className="min-[360px]:h-3 sm:h-3.5"
            />
          </div>

          <div className="min-w-0">
            <p
              className="
                whitespace-nowrap
                text-[6px]
                font-semibold
                text-[var(--text-primary)]/[0.80]

                min-[360px]:text-[7px]

                sm:text-[8px]
              "
            >
              Secure Architecture
            </p>

            <p
              className="
                mt-0.5
                whitespace-nowrap
                text-[5px]
                text-[var(--text-muted)]

                sm:text-[7px]
              "
            >
              Designed for reliability
            </p>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

// ============================================================
// HERO SECTION
// ============================================================

const HeroSection = () => {
  const { hero } = softwareDevelopmentData;

  // ============================================================
  // CONTACT MODAL STATE
  // ============================================================

  const [isContactModalOpen, setIsContactModalOpen] =
    useState(false);

  // ============================================================
  // PRIMARY BUTTON
  // ============================================================

  const handlePrimaryClick = () => {
    setIsContactModalOpen(true);
  };

  return (
    <section
      className="
        relative
        w-full
        overflow-hidden
        bg-[var(--bg-primary)]
        px-3
        pb-8
        pt-14

        min-[360px]:px-4
        min-[360px]:pt-16

        sm:px-6
        sm:pb-12
        sm:pt-20

        md:pb-14
        md:pt-24

        lg:px-8
        lg:pb-16
        lg:pt-28
      "
    >
      {/* ====================================================
          HERO BACKGROUND
      ===================================================== */}

      <div
        className="
          pointer-events-none
          absolute
          inset-0
          -z-10
          bg-[var(--bg-primary)]
        "
      >
        {/* LEFT GLOW */}

        <div
          className="
            absolute
            left-[5%]
            top-[5%]
            h-[240px]
            w-[240px]
            rounded-full
            bg-orange-600/[0.035]
            blur-[90px]

            sm:h-[320px]
            sm:w-[320px]
            sm:blur-[110px]

            lg:h-[380px]
            lg:w-[380px]
            lg:blur-[120px]
          "
        />

        {/* RIGHT GLOW */}

        <div
          className="
            absolute
            right-[-10%]
            top-[15%]
            h-[280px]
            w-[280px]
            rounded-full
            bg-orange-500/[0.025]
            blur-[100px]

            sm:h-[350px]
            sm:w-[350px]
            sm:blur-[120px]

            lg:right-0
            lg:h-[420px]
            lg:w-[420px]
            lg:blur-[130px]
          "
        />

        {/* GRID */}

        <div
          className="
            absolute
            inset-0
            opacity-[0.025]
            [background-image:linear-gradient(rgba(128,128,128,.45)_1px,transparent_1px),linear-gradient(90deg,rgba(128,128,128,.45)_1px,transparent_1px)]
            [background-size:45px_45px]

            sm:[background-size:60px_60px]

            lg:[background-size:70px_70px]
          "
        />
      </div>

      {/* ====================================================
          CONTENT
      ===================================================== */}

      <div
        className="
          mx-auto
          flex
          min-h-0
          w-full
          max-w-7xl
          items-center
          px-0
          py-6

          sm:min-h-[410px]
          sm:py-10

          md:min-h-[430px]
          md:py-12

          lg:min-h-[500px]
          lg:py-14
        "
      >
        <div
          className="
            grid
            w-full
            items-center
            gap-10

            md:grid-cols-[1fr_1fr]
            md:gap-8

            lg:grid-cols-[1.02fr_.98fr]
            lg:gap-10

            xl:gap-14
          "
        >
          {/* ==================================================
              LEFT CONTENT
          =================================================== */}

          <motion.div
            initial="hidden"
            animate="visible"
            variants={containerVariants}
            className="
              min-w-0
              text-center
              lg:text-left
            "
          >
            {/* =================================================
                EYEBROW
            ================================================== */}

            <motion.div variants={fadeUp}>
              <span
                className="
                  inline-flex
                  max-w-full
                  items-center
                  gap-1.5
                  rounded-full
                  border
                  border-orange-500/20
                  bg-orange-500/[0.06]
                  px-2.5
                  py-1.5
                  text-[7px]
                  font-semibold
                  uppercase
                  tracking-[0.15em]
                  text-orange-400

                  min-[360px]:gap-2
                  min-[360px]:px-3
                  min-[360px]:text-[8px]

                  sm:px-3.5
                  sm:py-2
                  sm:text-[10px]
                  sm:tracking-[0.22em]
                "
              >
                <span className="h-1 w-1 shrink-0 rounded-full bg-orange-400 sm:h-1.5 sm:w-1.5" />

                <span className="truncate">
                  {hero.eyebrow}
                </span>
              </span>
            </motion.div>

            {/* =================================================
                HEADING
            ================================================== */}

            <motion.h1
              variants={fadeUp}
              className="
                mx-auto
                mt-4
                max-w-3xl
                text-[2rem]
                font-bold
                leading-[1.08]
                tracking-tight
                text-[var(--text-primary)]

                min-[360px]:text-[2.15rem]

                sm:mt-5
                sm:text-4xl

                md:text-4xl

                lg:mx-0
                lg:text-6xl

                xl:text-[4.25rem]
              "
            >
              {hero.title}

              <br />

              <span className="text-orange-500">
                {hero.highlightedTitle}
              </span>
            </motion.h1>

            {/* =================================================
                DESCRIPTION
            ================================================== */}

            <motion.p
              variants={fadeUp}
              className="
                mx-auto
                mt-4
                max-w-2xl
                text-[11px]
                leading-[1.7]
                text-[var(--text-secondary)]

                min-[360px]:text-xs

                sm:mt-5
                sm:text-sm
                sm:leading-6

                md:text-base
                md:leading-7

                lg:mx-0
                lg:text-lg
              "
            >
              {hero.description}
            </motion.p>

            {/* =================================================
                BUTTONS
            ================================================== */}

            <motion.div
              variants={fadeUp}
              className="
                mt-5
                flex
                flex-col
                gap-2.5

                min-[400px]:flex-row
                min-[400px]:flex-wrap

                sm:mt-7
                sm:gap-4

                lg:justify-start
              "
            >
              {/* =================================================
                  START PROJECT BUTTON
              ================================================== */}

              <button
                type="button"
                onClick={handlePrimaryClick}
                className="
                  group
                  inline-flex
                  w-full
                  items-center
                  justify-center
                  gap-2
                  rounded-xl
                  bg-orange-500
                  min-h-11
                  px-4
                  py-2.5
                  text-xs
                  font-semibold
                  text-black
                  shadow-lg
                  shadow-orange-500/20
                  transition-all
                  duration-300
                  hover:-translate-y-0.5
                  hover:bg-orange-400
                  active:scale-[0.98]

                  min-[400px]:w-auto

                  sm:gap-2.5
                  sm:px-6
                  sm:py-3.5
                  sm:text-base
                "
              >
                {hero.primaryButton}

                <span
                  className="
                    flex
                    h-5
                    w-5
                    items-center
                    justify-center
                    rounded-full
                    bg-black
                    text-white

                    sm:h-6
                    sm:w-6
                  "
                >
                  <ArrowUpRight
                    size={11}
                    className="sm:h-[13px] sm:w-[13px]"
                  />
                </span>
              </button>
            </motion.div>

            {/* =================================================
                TRUST LINE
            ================================================== */}

            <motion.div
              variants={fadeUp}
              className="
                mt-5
                flex
                flex-wrap
                items-center
                justify-center
                gap-1.5
                text-[7px]
                text-[var(--text-muted)]

                min-[360px]:gap-2
                min-[360px]:text-[8px]

                sm:mt-6
                sm:text-[9px]

                lg:justify-start
              "
            >
              &nbsp;
            </motion.div>
          </motion.div>

          {/* ==================================================
              RIGHT PREMIUM SOFTWARE VISUAL
          =================================================== */}

          <motion.div
            initial={{
              opacity: 0,
              y: 30,
            }}
            whileInView={{
              opacity: 1,
              y: 0,
            }}
            viewport={{
              once: true,
              amount: 0.15,
            }}
            transition={{
              duration: 0.8,
              ease: [0.22, 1, 0.36, 1],
            }}
            className="
              relative
              min-w-0
              w-full
              px-0
              md:self-center

              min-[360px]:px-1

              sm:px-3

              md:px-1

              lg:px-0
            "
          >
            <HeroSoftwareVisual />
          </motion.div>
        </div>
      </div>

    

      <ContactModal
        isOpen={isContactModalOpen}
        onClose={() => setIsContactModalOpen(false)}
        initialService="Software Development"
        initialSubService=""
      />
    </section>
  );
};

export default HeroSection;